import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { addSchema, AddModelFormValues } from "./validations";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  COMPANY,
  DATE,
  DESCRIPTION,
  TITLE,
  TYPE,
  UPLOAD,
  YEAR,
} from "./consts";
import { routes } from "@/routes/utils";
import { toast } from "sonner";
import {
  useAddNewsMediaImageMutation,
  useAddNewsMediaMutation,
  useGetNewsMediaById,
  useGetNewsMediaImageById,
  useUpdateNewsMediaMutation,
} from "@/services";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useLocation } from "react-router-dom";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isView = action == "view";
  const isEdit = action == "edit";
  const isAdd = action == "add";
  const isMobile = useIsMobile();
  const uploadRef = useRef<any>(null);
  const [_attachmentId, setAttachmentId] = useState<string>("");
  const [openCropper, setOpenCropper] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string>("");
  const [orgImageName, setOrgImageName] = useState<string>("");
  const [openImageViewer, setOpenImageViewer] = useState<boolean>(false);
  const [showLink, setShowLink] = useState<boolean>(!isAdd);

  const location = useLocation();
  const applicableCompany = location.state?.applicableCompany;

  const form = useForm<AddModelFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [TITLE]: "",
      [DATE]: "",
      [YEAR]: "",
      [TYPE]: "",
      [COMPANY]: "",
      [UPLOAD]: "",
      [DESCRIPTION]: "",
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.newsMedia}`);
  };

  const { data: newsMediaData, isLoading: isNewsMediaDataLoading } =
    useGetNewsMediaById({
      id: id as string,
      queryParams: {
        enabled: !!id,
      },
    });

  const { data: newsImage, isLoading: isImageLoading } =
    useGetNewsMediaImageById({
      id: form.watch(UPLOAD)?.attachmentId as string,
      queryParams: {
        enabled: !!form.watch(UPLOAD)?.attachmentId,
      },
    });

  useEffect(() => {
    const loadImage = async () => {
      if (!newsImage) return;

      const blob = await newsImage?.blob();
      const url = URL.createObjectURL(blob);
      setImageSrc(url);
    };

    loadImage();
  }, [newsImage]);

  useEffect(() => {
    if (newsMediaData) {
      form.reset({
        [TITLE]: newsMediaData?.data?.[TITLE]?.toString() || "",
        [DATE]: newsMediaData?.data?.[DATE]?.toString() || "",
        [YEAR]: newsMediaData?.data?.[YEAR]?.toString() || "",
        [TYPE]: newsMediaData?.data?.[TYPE]?.toString() || "",
        [COMPANY]: newsMediaData?.data?.[COMPANY]?.toString() || "",
        [UPLOAD]: {
          attachmentId: newsMediaData?.data?.[UPLOAD]?.toString() || "",
          name: newsMediaData?.data?.attachmentData?.fileName?.toString() || "",
        },
        [DESCRIPTION]: newsMediaData?.data?.[DESCRIPTION]?.toString() || "",
      });
      setAttachmentId(newsMediaData?.data?.[UPLOAD]?.toString());
    }
  }, [newsMediaData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddNewsMediaMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.issues?.error === "string"
          ? error?.response?.data?.issues?.error
          : typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong",
        {
          className: "!bg-red-500 !text-white",
        }
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast.success("News & Media Added Successfully", {
        className: "!bg-green-600 !text-white",
      });
      queryClient.invalidateQueries({
        queryKey: ["news-media"],
      });
      onBackClick();
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateNewsMediaMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("News & Media Updated Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({
          queryKey: ["news-media"],
        });
        onBackClick();
      },
    });

  const { mutate: addImageInfo, isPending: isAddImagePending } =
    useAddNewsMediaImageMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        const values = form.watch();

        if (id) {
          updateInfo({
            id,
            data: {
              ...values,
              [UPLOAD]: data?.data?.data?.attachmentId,
              [DATE]: moment(values[DATE]).format("YYYY-MM-DD"),
            },
          });
        } else {
          addInfo({
            data: {
              ...values,
              [UPLOAD]: data?.data?.data?.attachmentId,
              [DATE]: moment(values[DATE]).format("YYYY-MM-DD"),
              applicableCompany: applicableCompany === "MBAPL" ? "MBAPL" : applicableCompany === "KPL" ? "KPL" : "OSTWAL",
            },
          });
        }
      },
    });

  const onSubmit = (data: AddModelFormValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(data);

    if (!_data[UPLOAD]) {
      form.setError(UPLOAD, {
        message: "This Field is required",
      });
      return;
    }

    if (id) {
      if (
        form.watch(UPLOAD)?.name !=
        newsMediaData?.data?.attachmentData?.fileName
      ) {
        addImageInfo({ data: { file: _data[UPLOAD] } });
      } else {
        updateInfo({
          id,
          data: {
            ..._data,
            [UPLOAD]: newsMediaData?.data?.attachmentId,
            [DATE]: moment(_data[DATE] || "").format("YYYY-MM-DD"),
          },
        });
      }
    } else {
      addImageInfo({ data: { file: _data[UPLOAD] } });
    }
  };

  const onFileChange = (selectedFile: any) => {
    setOrgImageName(selectedFile?.name || "cropped_image.jpg");
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
    setOpenCropper(true);
    setShowLink(false);
  };

  const onViewImage = () => {
    setOpenImageViewer(true);
  };

  const onCloseCropper = () => {
    setOpenCropper(false);
    form.setValue(UPLOAD, "");
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isAddPending,
    isMobile,
    uploadRef,
    isAddImagePending,
    isNewsMediaDataLoading,
    action,
    isUpdatePending,
    isEdit,
    onFileChange,
    openCropper,
    setOpenCropper,
    imageSrc,
    orgImageName,
    onViewImage,
    isAdd,
    openImageViewer,
    setOpenImageViewer,
    isImageLoading,
    onCloseCropper,
    newsMediaData,
    showLink,
  };
};

export default useAddEdit;
